class ThemeVideoInline extends HTMLElement {
    connectedCallback() {
        this.trigger = this.querySelector('.theme-video__poster-trigger');
        this.container = this.querySelector('[data-video-target]') || this;
        this.videoElement = this.querySelector('video');
        this.iframeElement = this.querySelector('iframe');
        this.autoplayScroll = this.hasAttribute('data-autoplay-scroll') || this.dataset.autoplayScroll === 'true';

        this.isPlayPending = false;
        this.hasStartedPlayback = false;

        // Always attach click listener to trigger for manual click playback
        if (this.trigger) {
            this.trigger.addEventListener('click', (e) => {
                e.preventDefault();
                this.startPlayback(true);
            });
        }

        // Configure HTML5 video attributes for iOS Safari WebKit
        if (this.videoElement) {
            this.configureVideoAttributes(this.videoElement);

            const hidePoster = () => this.hidePoster();

            this.videoElement.addEventListener('playing', hidePoster);
            this.videoElement.addEventListener('timeupdate', () => {
                if (this.videoElement && this.videoElement.currentTime > 0) {
                    hidePoster();
                }
            });

            // Check initial position on page load
            const rect = this.getBoundingClientRect();
            const windowHeight = window.innerHeight || document.documentElement.clientHeight;
            const inInitialViewport = rect.top < windowHeight * 0.85 && rect.bottom > 0;

            if (inInitialViewport) {
                this.startPlayback(false);
            }
        }

        if (this.autoplayScroll) {
            this.initScrollObserver();
        }
    }

    configureVideoAttributes(video) {
        if (!video) return;
        video.muted = true;
        video.defaultMuted = true;
        video.playsInline = true;
        video.webkitPlaysInline = true;
        video.setAttribute('muted', '');
        video.setAttribute('playsinline', '');
        video.setAttribute('webkit-playsinline', '');
        video.setAttribute('disablepictureinpicture', '');
    }

    hidePoster() {
        if (this.trigger && this.trigger.style.display !== 'none') {
            this.trigger.style.pointerEvents = 'none';
            this.trigger.style.opacity = '0';
            setTimeout(() => {
                if (this.trigger) this.trigger.style.display = 'none';
            }, 400);
        }
    }

    initScrollObserver() {
        const isVideoPlaying = () => {
            if (this.videoElement) {
                return (this.hasStartedPlayback || !this.videoElement.paused) && this.videoElement.currentTime > 0;
            }
            if (this.iframeElement) {
                return this.iframeStarted === true;
            }
            return false;
        };

        const checkAndPlay = () => {
            if (isVideoPlaying() || this.isPlayPending) return;

            const rect = this.getBoundingClientRect();
            const windowHeight = window.innerHeight || document.documentElement.clientHeight;

            // Trigger when section enters top 85% of viewport
            const isInViewport = rect.top < windowHeight * 0.85 && rect.bottom > 0;

            if (isInViewport) {
                this.startPlayback(false);
            }
        };

        // 1. IntersectionObserver to detect when section enters viewport
        if ('IntersectionObserver' in window) {
            this.observer = new IntersectionObserver((entries) => {
                entries.forEach(entry => {
                    if (entry.isIntersecting && !isVideoPlaying() && !this.isPlayPending) {
                        this.startPlayback(false);
                    }
                });
            }, {
                root: null,
                rootMargin: '0px 0px -10% 0px',
                threshold: 0.05
            });
            this.observer.observe(this);
        }

        // 2. Passive scroll & touch event listeners for iOS Safari WebKit
        const handleScrollOrTouch = () => {
            if (isVideoPlaying()) {
                window.removeEventListener('scroll', handleScrollOrTouch);
                window.removeEventListener('touchmove', handleScrollOrTouch);
                window.removeEventListener('touchend', handleScrollOrTouch);
                if (this.observer) this.observer.disconnect();
                return;
            }
            checkAndPlay();
        };

        window.addEventListener('scroll', handleScrollOrTouch, { passive: true });
        window.addEventListener('touchmove', handleScrollOrTouch, { passive: true });
        window.addEventListener('touchend', handleScrollOrTouch, { passive: true });

        // Check immediately on load
        checkAndPlay();
    }

    startPlayback(userInitiated = false) {
        // 1. Handle HTML5 Video (MP4)
        if (this.videoElement) {
            if (!this.videoElement.paused && this.videoElement.currentTime > 0 && !userInitiated) {
                this.hidePoster();
                return;
            }

            // Prevent duplicate play calls while a play promise is pending
            if (this.isPlayPending && !userInitiated) {
                return;
            }

            this.configureVideoAttributes(this.videoElement);
            this.isPlayPending = true;

            const playPromise = this.videoElement.play();
            if (playPromise !== undefined) {
                playPromise.then(() => {
                    this.isPlayPending = false;
                    this.hasStartedPlayback = true;
                    this.hidePoster();
                }).catch((error) => {
                    this.isPlayPending = false;
                    console.warn("Autoplay promise waiting on iOS:", error);
                    if (this.videoElement) {
                        this.configureVideoAttributes(this.videoElement);
                        setTimeout(() => {
                            if (this.videoElement && (this.videoElement.paused || !this.hasStartedPlayback)) {
                                this.configureVideoAttributes(this.videoElement);
                                this.videoElement.play().then(() => {
                                    this.hasStartedPlayback = true;
                                    this.hidePoster();
                                }).catch(() => { });
                            }
                        }, 150);
                    }
                });
            } else {
                this.isPlayPending = false;
                this.hasStartedPlayback = true;
                this.hidePoster();
            }
            return;
        }

        // 2. Handle YouTube / Vimeo Iframe Embeds
        if (this.iframeElement) {
            this.iframeStarted = true;
            const origin = encodeURIComponent(window.location.origin);

            if (this.iframeElement.dataset.src) {
                let src = this.iframeElement.dataset.src;
                if (src.indexOf('origin=') === -1) {
                    src += (src.indexOf('?') === -1 ? '?' : '&') + 'origin=' + origin;
                }
                this.iframeElement.src = src;
                delete this.iframeElement.dataset.src;
            } else if (this.iframeElement.src && this.iframeElement.src.indexOf('origin=') === -1) {
                this.iframeElement.src += (this.iframeElement.src.indexOf('?') === -1 ? '?' : '&') + 'origin=' + origin;
            }

            const sendYouTubeCommands = () => {
                try {
                    if (this.iframeElement && this.iframeElement.contentWindow) {
                        this.iframeElement.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'mute', args: [] }), '*');
                        this.iframeElement.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'playVideo', args: [] }), '*');
                    }
                } catch (e) { }
            };

            this.hidePoster();
            sendYouTubeCommands();

            this.iframeElement.addEventListener('load', () => {
                this.hidePoster();
                sendYouTubeCommands();
                setTimeout(sendYouTubeCommands, 200);
                setTimeout(sendYouTubeCommands, 500);
                setTimeout(sendYouTubeCommands, 1000);
            });

            setTimeout(sendYouTubeCommands, 300);
            setTimeout(sendYouTubeCommands, 600);
            setTimeout(sendYouTubeCommands, 1200);
            setTimeout(sendYouTubeCommands, 2000);
            return;
        }

        // 3. Dynamic Creation Fallback (If video source URL is passed dynamically)
        const url = this.getAttribute('data-video-url') || '';
        const title = this.getAttribute('data-video-title') || 'Video';
        const embed = this.getEmbedSrc(url);

        if (!url && !embed) return;

        const playBtn = this.querySelector('.theme-video__play-button');
        if (playBtn) playBtn.style.display = 'none';

        if (embed) {
            this.iframeStarted = true;
            const iframe = document.createElement('iframe');
            this.iframeElement = iframe;
            iframe.className = 'theme-video__iframe';
            iframe.src = embed;
            iframe.title = title;
            iframe.allow = 'autoplay; encrypted-media; picture-in-picture; fullscreen';
            iframe.allowFullscreen = true;
            // iframe.style.cssText = 'width: 100%; height: 100%; position: absolute; top: 0; left: 0; opacity: 0; transition: opacity 0.6s ease; z-index: 1;';
            iframe.style.cssText = 'width: 100%; height: 100%; position: absolute; top: 0; left: 0; opacity: 0; transition: opacity 0.6s ease; z-index: 1; pointer-events: none;';

            const sendYouTubeCommands = () => {
                try {
                    if (iframe.contentWindow) {
                        iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'mute', args: [] }), '*');
                        iframe.contentWindow.postMessage(JSON.stringify({ event: 'command', func: 'playVideo', args: [] }), '*');
                    }
                } catch (e) { }
            };

            iframe.addEventListener('load', () => {
                this.hidePoster();
                iframe.style.opacity = '1';
                sendYouTubeCommands();
                setTimeout(sendYouTubeCommands, 200);
                setTimeout(sendYouTubeCommands, 500);
                setTimeout(sendYouTubeCommands, 1000);
            });

            this.container.appendChild(iframe);
            sendYouTubeCommands();
            setTimeout(sendYouTubeCommands, 300);
            setTimeout(sendYouTubeCommands, 600);
            return;
        }

        if (url) {
            const video = document.createElement('video');
            this.videoElement = video;
            video.className = 'theme-video__video';
            video.preload = 'auto';
            video.controls = true;

            this.configureVideoAttributes(video);
            video.setAttribute('loop', '');
            video.loop = true;
            video.style.cssText = 'width: 100%; height: 100%; position: absolute; object-fit: cover; opacity: 0; transition: opacity 0.6s ease; z-index: 1;';

            video.addEventListener('playing', () => {
                video.style.opacity = '1';
                this.hidePoster();
            });

            this.container.appendChild(video);
            video.src = url;

            const source = document.createElement('source');
            source.src = url;
            source.type = 'video/mp4';
            video.appendChild(source);

            video.load();
            this.isPlayPending = true;
            video.play().then(() => {
                this.isPlayPending = false;
                this.hasStartedPlayback = true;
                this.hidePoster();
            }).catch(() => {
                this.isPlayPending = false;
            });
        }
    }

    playVideo(userInitiated) {
        this.startPlayback(userInitiated);
    }

    getEmbedSrc(url) {
        if (!url) return null;

        let parsed;
        let host = '';
        try {
            parsed = new URL(url, window.location.origin);
            host = parsed.hostname;
        } catch (_error) {
            host = '';
        }

        const youTubeId = this.getYouTubeId(url, parsed);
        if (youTubeId) {
            const origin = encodeURIComponent(window.location.origin);
            // return 'https://www.youtube-nocookie.com/embed/' + youTubeId + '?autoplay=1&mute=1&playsinline=1&enablejsapi=1&playlist=' + youTubeId + '&loop=1&controls=1&rel=0&modestbranding=1&origin=' + origin;
            return 'https://www.youtube-nocookie.com/embed/' + youTubeId + '?autoplay=1&mute=1&playsinline=1&enablejsapi=1&playlist=' + youTubeId + '&loop=1&controls=0&rel=0&modestbranding=1&iv_load_policy=3&vq=hd1080&origin=' + origin;
        }

        if (host.indexOf('vimeo.com') !== -1) {
            const pathname = parsed && parsed.pathname ? parsed.pathname : '';
            const pathParts = pathname.split('/').filter(Boolean);
            const vimeoId = pathParts.length > 0 ? pathParts[pathParts.length - 1] : '';
            if (vimeoId && /^\d+$/.test(vimeoId)) {
                return 'https://player.vimeo.com/video/' + vimeoId + '?autoplay=1&muted=1&playsinline=1&autopause=0&loop=1';
            }
        }

        if (host.indexOf('youtube') !== -1 || host.indexOf('player.vimeo') !== -1) {
            return url;
        }

        return null;
    }

    getYouTubeId(url, parsed) {
        if (!parsed) return '';

        const host = parsed.hostname ? parsed.hostname.toLowerCase() : '';
        const isYouTubeHost =
            host.indexOf('youtube.com') !== -1 ||
            host.indexOf('youtube-nocookie.com') !== -1 ||
            host.indexOf('youtu.be') !== -1;

        if (!isYouTubeHost) {
            return '';
        }

        if (host.indexOf('youtu.be') !== -1) {
            const pathPart = parsed.pathname ? parsed.pathname.replace('/', '').split('/')[0] : '';
            return pathPart || '';
        }

        if (parsed.searchParams && parsed.searchParams.has('v')) {
            return parsed.searchParams.get('v') || '';
        }

        const embedMatch = parsed.pathname ? parsed.pathname.match(/\/embed\/([^/?]+)/) : null;
        if (embedMatch && embedMatch[1]) return embedMatch[1];

        const shortsMatch = parsed.pathname ? parsed.pathname.match(/\/shorts\/([^/?]+)/) : null;
        if (shortsMatch && shortsMatch[1]) return shortsMatch[1];

        return '';
    }
}

if (!customElements.get('theme-video-inline')) {
    customElements.define('theme-video-inline', ThemeVideoInline);
}
